import { useState, useEffect } from "react";
import moment from "moment-timezone";
import { detectLiquidityZones } from "./detectLiquidityZones";

export const useLiquidityZoneData = (
  currencyPair,
  timeInterval,
  timeZone,
  stdDevMultiplier,
  persistenceThreshold,
  dateRange,
  setDateRange,
  currentPrice, setCurrentPrice
) => {
  const [rawData, setRawData] = useState(null); // Store raw data
  const [chartData, setChartData] = useState(null); // Store chart-ready data
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [refreshCounter, setRefreshCounter] = useState(60); // Countdown for the next refresh

  // Fetch raw data function
  const fetchRawData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("https://vidplay-api.deng-software.com/fx", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ currencyPair, timeInterval }),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setRawData(
        data.data.map((point) => ({
          time: moment.tz(point.time, "UTC").tz(timeZone).format("YYYY-MM-DD HH:mm:ss"),
          open: point.open,
          high: point.high,
          low: point.low,
          close: point.close,
          volume: point.volume,
        }))
      );
    } catch (err) {
      setError(`Failed to fetch chart data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data initially and every 60 seconds
  useEffect(() => {
    fetchRawData(); // Initial fetch

    const interval = setInterval(() => {
      fetchRawData(); // Fetch data every 60 seconds
      setRefreshCounter(60); // Reset the counter after fetching
    }, 60000); // 60 seconds

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, [currencyPair, timeInterval, timeZone]);

  // Countdown timer for refresh
  useEffect(() => {
    const countdown = setInterval(() => {
      setRefreshCounter((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000); // Update every second

    return () => clearInterval(countdown); // Cleanup on unmount
  }, []);

  // Prepare chart data from raw data
  useEffect(() => {
    if (!rawData) return;

    try {
      const sortedData = rawData.sort((a, b) => new Date(a.time) - new Date(b.time));
      const { liquidityZones } = detectLiquidityZones(
        sortedData,
        persistenceThreshold,
        stdDevMultiplier
      );

      // Set the current price
      const latestDataPoint = sortedData[sortedData.length - 1];
      if (latestDataPoint) {
        setCurrentPrice(latestDataPoint.close);
      }

      // Set the initial date range to cover the processed data if not already set
      if (!dateRange.start || !dateRange.end) {
        setDateRange({
          start: sortedData[0]?.time,
          end: sortedData[sortedData.length - 1]?.time,
        });
      }

      const traceCandlesticks = {
        x: sortedData.map((point) => point.time),
        open: sortedData.map((point) => point.open),
        high: sortedData.map((point) => point.high),
        low: sortedData.map((point) => point.low),
        close: sortedData.map((point) => point.close),
        type: "candlestick",
        name: `${currencyPair} Candlesticks`,
      };

      const demandZoneTraces = liquidityZones
        .filter((zone) => zone.type === "demand")
        .flatMap((zone) => [
          {
            x: [zone.startTime, zone.endTime],
            y: [zone.proximal, zone.proximal],
            type: "scatter",
            mode: "lines",
            line: { color: "green", width: 2 },
            name: "Demand Zone Proximal",
            hovertext: `Type: Demand<br>Start: ${zone.startTime}<br>End: ${zone.endTime}<br>Proximal: ${zone.proximal}<br>Distal: ${zone.distal}<br>Width: ${zone.width}<br>Persistence: ${zone.persistence}`,
            hoverinfo: "text",
          },
          {
            x: [zone.startTime, zone.endTime],
            y: [zone.distal, zone.distal],
            type: "scatter",
            mode: "lines",
            line: { color: "green", width: 1, dash: "dot" },
            name: "Demand Zone Distal",
            hovertext: `Type: Demand<br>Start: ${zone.startTime}<br>End: ${zone.endTime}<br>Proximal: ${zone.proximal}<br>Distal: ${zone.distal}<br>Width: ${zone.width}<br>Persistence: ${zone.persistence}`,
            hoverinfo: "text",
          },
        ]);

      const supplyZoneTraces = liquidityZones
        .filter((zone) => zone.type === "supply")
        .flatMap((zone) => [
          {
            x: [zone.startTime, zone.endTime],
            y: [zone.proximal, zone.proximal],
            type: "scatter",
            mode: "lines",
            line: { color: "red", width: 2 },
            name: "Supply Zone Proximal",
            hovertext: `Type: Supply<br>Start: ${zone.startTime}<br>End: ${zone.endTime}<br>Proximal: ${zone.proximal}<br>Distal: ${zone.distal}<br>Width: ${zone.width}<br>Persistence: ${zone.persistence}`,
            hoverinfo: "text",
          },
          {
            x: [zone.startTime, zone.endTime],
            y: [zone.distal, zone.distal],
            type: "scatter",
            mode: "lines",
            line: { color: "red", width: 1, dash: "dot" },
            name: "Supply Zone Distal",
            hovertext: `Type: Supply<br>Start: ${zone.startTime}<br>End: ${zone.endTime}<br>Proximal: ${zone.proximal}<br>Distal: ${zone.distal}<br>Width: ${zone.width}<br>Persistence: ${zone.persistence}`,
            hoverinfo: "text",
          },
        ]);

      const chartInfo = {
        traces: [traceCandlesticks, ...demandZoneTraces, ...supplyZoneTraces],
        layout: {
          xaxis: {
            title: `Time (${timeZone})`,
            type: "date",
            tickformat: "%b %d %H:%M",
            range: [dateRange.start, dateRange.end],
          },
          yaxis: { title: "Price", autorange: true },
          hovermode: "closest",
        },
      };

      setChartData(chartInfo);
    } catch (err) {
      setError(`Failed to process chart data: ${err.message}`);
    }
  }, [rawData, stdDevMultiplier, persistenceThreshold, dateRange]);

  return { chartData, loading, error, refreshCounter };
};
